/**
 * API client for backend communication.
 *
 * Provides type-safe methods for interacting with the backend REST API.
 * Uses fetch API with proper error handling and TypeScript types.
 */

import type {
  ApiError,
  AuthUser,
  BackupResponse,
  TenantObservabilityStats,
  TenantObservabilityLogEvent,
  Certificate,
  CertificateListResponse,
  ClientDatabase,
  ClientDatabaseCreateRequest,
  ClientDatabaseDeleteResponse,
  ClientDatabaseDetailResponse,
  ClientDatabaseListResponse,
  ClientDatabaseUser,
  ClientDatabaseUserCreateRequest,
  ClientDatabaseUserListResponse,
  ClientDatabaseUserUpdatePermissionsRequest,
  ClientOverview,
  Container,
  ContainerActionResponse,
  ContainerCreateRequest,
  ContainerListResponse,
  DbPasswordRotateRequest,
  Domain,
  DomainActionResponse,
  DomainCreateRequest,
  DomainDeleteRequest,
  DomainListResponse,
  ErrorResponse,
  GlobalSettings,
  GlobalSettingsUpdateRequest,
  LoginRequest,
  PmaSsoLinkResponse,
  RegisterRequest,
  RestoreJobResponse,
  SystemStats,
  TokenResponse,
  TwoFactorDisableRequest,
  TwoFactorDisableResponse,
  TwoFactorSetupResponse,
  TwoFactorVerifyRequest,
  TwoFactorVerifyResponse,
  User,
  UserCreateRequest,
  UserListResponse,
  AdminPasswordResetRequest,
  AdminPasswordResetResponse,
  Slot,
  DomainRequest,
  SiteCertificateOverview,
  DomainRequestKind,
} from './types';

// Base API URL (proxied through Vite dev server)
export const API_BASE_URL = '/api';

/**
 * Custom error class for API errors.
 */
class ApiErrorImpl extends Error implements ApiError {
  constructor(
    message: string,
    public status: number,
    public response?: ErrorResponse
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Handle authentication errors by clearing auth state and redirecting to login.
 *
 * F08-001 (2026-05-19): the JWT credential now lives in an HttpOnly
 * cookie set by the backend, not in localStorage. We only clear the
 * profile cache (authUser) here — the cookie is dropped by the
 * /api/auth/logout call OR by the server's max-age, neither of which
 * the JS layer controls.
 */
/**
 * Pages that a signed-out user is SUPPOSED to be able to sit on. A 401 while
 * on one of these is the expected steady state, not a session expiry.
 *
 * Without this exemption the boot-time `authAPI.me()` probe — which 401s for
 * every logged-out visitor — bounced `/register` straight to `/login`, making
 * the registration page unreachable for exactly the users who need it. That
 * had been true since the F08-001 cookie migration and was never caught,
 * because the register spec asserted on form fields and had not run since the
 * suite broke. Found 2026-08-01 while repairing the harness.
 */
const PUBLIC_PATHS = [
  '/login',
  '/register',
  '/admin/login',
  '/admin/register',
  // The break-glass Recovery page carries its own sign-in form. On the
  // recovery port /admin/login does not exist, so a 401 there must never
  // navigate away from the page that owns the form.
  '/admin/recovery',
];

/** The login screen belonging to whichever bundle is currently loaded. */
function loginPathForCurrentPlane(): string {
  return window.location.pathname.startsWith('/admin') ? '/admin/login' : '/login';
}

function handleAuthError(): void {
  localStorage.removeItem('authUser');

  // Already somewhere a signed-out user legitimately belongs — leave them be.
  if (PUBLIC_PATHS.includes(window.location.pathname)) {
    return;
  }

  // Plane-aware: bouncing an admin-bundle 401 to the tenant `/login` would
  // drop the operator into the wrong SPA and, after signing in, the wrong
  // console.
  window.location.href = loginPathForCurrentPlane();
}

/**
 * Read a Response body and try to parse it as JSON without ever letting a raw
 * parse error escape. Returns:
 *   { kind: 'empty' }                 — body was empty / whitespace
 *   { kind: 'json', data: unknown }   — body parsed as JSON
 *   { kind: 'text', text: string }    — body was non-empty but not valid JSON
 *
 * Per CLAUDE.md Non-Negotiable §9: the user must NEVER see "Failed to execute
 * 'json' on 'Response'" or similar raw browser errors.
 */
async function safeReadBody(
  response: Response,
): Promise<{ kind: 'empty' } | { kind: 'json'; data: unknown } | { kind: 'text'; text: string }> {
  let text: string;
  try {
    text = await response.text();
  } catch {
    return { kind: 'empty' };
  }
  if (!text || !text.trim()) {
    return { kind: 'empty' };
  }
  try {
    return { kind: 'json', data: JSON.parse(text) };
  } catch {
    return { kind: 'text', text };
  }
}

/**
 * Build a user-facing error message from a non-ok Response, after the body has
 * been safely read. Preserves the server's `detail` verbatim when present.
 */
function errorMessageFor(
  status: number,
  body: Awaited<ReturnType<typeof safeReadBody>>,
): { message: string; response?: ErrorResponse } {
  if (body.kind === 'json') {
    const errorResponse = body.data as ErrorResponse | undefined;
    const detail = errorResponse?.detail;
    if (typeof detail === 'string' && detail.length > 0) {
      return { message: detail, response: errorResponse };
    }
    // FastAPI validation errors (422): `detail` is a list of
    // {loc: [...], msg}. Show each field and message rather than a bare
    // "HTTP 422" — the server's explanation must reach the user (§9).
    if (Array.isArray(detail) && detail.length > 0) {
      const parts = (detail as Array<{ loc?: unknown[]; msg?: unknown }>).map((d) => {
        const field = Array.isArray(d?.loc) ? d.loc.filter((x) => x !== 'body').join('.') : '';
        const msg = typeof d?.msg === 'string' ? d.msg : JSON.stringify(d);
        return field ? `${field}: ${msg}` : msg;
      });
      return { message: parts.join('; '), response: errorResponse };
    }
  }
  // Body was empty, non-JSON, or JSON-without-detail — fall back to a clean,
  // status-tied message. Never leak the raw text/HTML body to the user.
  if (status === 401 || status === 403) {
    return { message: 'Authentication required' };
  }
  if (status >= 500) {
    return { message: 'Server error: please try again in a moment' };
  }
  if (status === 0) {
    return { message: 'Unable to reach server' };
  }
  return { message: `Request failed (HTTP ${status})` };
}

export interface FetchAPIOptions extends RequestInit {
  /**
   * Do not tear down the session and bounce to /login if this request 401s.
   *
   * For AMBIENT requests only — background/optional fetches whose failure
   * says nothing about whether the user's session is alive. Plugin discovery
   * is the motivating case: `/api/plugins/*` fires on every page mount, and
   * because it went through the default path a single 401 from it logged the
   * user out of a page they were legitimately viewing. That made an optional
   * subsystem able to eject the user from the panel — "plugins must not break
   * the panel" (bug #3) applies to the session as much as to the layout.
   *
   * The user-facing request that actually needs a live session will 401 on
   * its own and redirect properly.
   */
  suppressAuthRedirect?: boolean;
}

/**
 * Generic fetch wrapper with error handling.
 *
 * @param url - API endpoint URL
 * @param options - Fetch options
 * @returns Parsed JSON response
 * @throws ApiError if request fails
 */
/** Dispatched on window when the API answers 403 password_change_required. */
export const PASSWORD_CHANGE_REQUIRED_EVENT = 'hpanel:password-change-required';

export async function fetchAPI<T>(url: string, options: FetchAPIOptions = {}): Promise<T> {
  const defaultHeaders: Record<string, string> = {};

  // Don't set Content-Type for FormData — the browser must set it with the
  // multipart boundary. For all other bodies, default to JSON.
  if (!(options.body instanceof FormData)) {
    defaultHeaders['Content-Type'] = 'application/json';
  }

  // F08-001 (2026-05-19): the JWT used to live in localStorage and be
  // injected as `Authorization: Bearer …`. It now lives in an HttpOnly
  // cookie minted by the backend on login (P27). We send credentials
  // on every request so the cookie is carried automatically; XSS can
  // no longer lift the credential because JS can't read HttpOnly
  // cookies.
  //
  // The Authorization header is intentionally NOT set anymore — the
  // backend accepts EITHER (Bearer header or cookie), and the cookie
  // is preferable from a security standpoint. Tests that mint tokens
  // and pass them as Bearer keep working because the backend still
  // honors the header.

  const config: RequestInit = {
    ...options,
    credentials: options.credentials ?? 'include',
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  let response: Response;
  try {
    response = await fetch(url, config);
  } catch (error) {
    // Network-layer failure (DNS, connection refused, CORS, offline, abort).
    // Browsers throw TypeError here; never surface raw error.message to the user.
    // BUT: full original error MUST reach the dev console so the next person
    // debugging "Unable to reach server" has a fighting chance — without it,
    // CORS/redirect/abort all look identical at the UI layer (CLAUDE.md §9).
    // eslint-disable-next-line no-console
    console.error('[fetchAPI] network-layer failure for', url, error);
    throw new ApiErrorImpl('Unable to reach server', 0);
  }

  // 204 No Content — nothing to parse.
  if (response.status === 204) {
    return undefined as T;
  }

  const body = await safeReadBody(response);

  if (!response.ok) {
    const { message, response: errorResponse } = errorMessageFor(response.status, body);
    // Dev-console contract (§9): the user sees a clean message; the console
    // keeps what actually came back.
    // eslint-disable-next-line no-console
    console.error(
      '[fetchAPI] HTTP', response.status, 'for', url, '—',
      body.kind === 'json' ? body.data : body.kind === 'text' ? body.text.slice(0, 200) : '(empty body)',
    );

    // Side effect for AUTHENTICATION failures (401): clear local auth state
    // and bounce to /login. We deliberately do NOT trigger this on 403:
    // 403 means "authenticated but not authorized for this specific action"
    // (e.g. "Domain not owned by user" when a tenant POSTs to a domain they
    // don't own). Treating 403 as a logout caused the May 2026 regression
    // where creating a mailbox on the wrong domain silently kicked the
    // user back to /login mid-flow.
    if (response.status === 401 && !options.suppressAuthRedirect) {
      handleAuthError();
    }
    // The account still uses a password someone else set: tell the app (it
    // shows the change-password page). Navigation stays with the router.
    if (response.status === 403 && message === 'password_change_required') {
      window.dispatchEvent(new Event(PASSWORD_CHANGE_REQUIRED_EVENT));
    }

    throw new ApiErrorImpl(message, response.status, errorResponse);
  }

  // 2xx but non-JSON body — treat empty as undefined, anything else is a contract
  // violation we cannot meaningfully type. Return undefined for the empty case;
  // callers that expect data will fail at the use site, which is the correct
  // place to notice a backend regression.
  if (body.kind === 'empty') {
    return undefined as T;
  }
  if (body.kind === 'text') {
    // eslint-disable-next-line no-console
    console.error('[fetchAPI] non-JSON 2xx response from', url, '— first 200 chars:', body.text.slice(0, 200));
    throw new ApiErrorImpl('Server returned an unexpected response format', response.status);
  }
  return body.data as T;
}

/**
 * Blob fetch variant for binary download endpoints (e.g. backup tarballs).
 *
 * Applies the same §9 resilience contract as fetchAPI:
 *   - TypeError (network down / CORS / redirect)  → "Unable to reach server"
 *   - non-2xx → safeReadBody + errorMessageFor → clean, status-tied message
 *   - 2xx → response.blob() (never calls .json() on a binary stream)
 *   - Every error is console.error'd before throwing
 *
 * Returns { blob, filename } where filename comes from Content-Disposition or
 * falls back to fallbackFilename.
 */
export async function fetchAPIBlob(
  url: string,
  fallbackFilename = 'download.bin',
  // F08-011: optional extra headers so the caller can plumb
  // bearer-equivalent download tokens through a header instead of
  // the query string (which leaks via Referer, browser history,
  // proxy logs, etc.). Existing callers that don't pass headers
  // keep the cookie-only behavior unchanged.
  extraHeaders: Record<string, string> = {},
): Promise<{ blob: Blob; filename: string }> {
  // F08-001: cookie-based credential, same logic as fetchAPI.
  // credentials=include sends the HttpOnly auth cookie on every
  // download request.
  let response: Response;
  try {
    response = await fetch(url, {
      headers: extraHeaders,
      credentials: 'include',
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[fetchAPIBlob] network-layer failure for', url, error);
    throw new ApiErrorImpl('Unable to reach server', 0);
  }

  if (!response.ok) {
    const body = await safeReadBody(response);
    const { message } = errorMessageFor(response.status, body);
    if (response.status === 401) {
      // See fetchAPI — 403 is NOT a logout trigger (auth'd but unauthorized).
      handleAuthError();
    }
    // eslint-disable-next-line no-console
    console.error('[fetchAPIBlob] non-ok', response.status, 'for', url);
    throw new ApiErrorImpl(message, response.status);
  }

  let blob: Blob;
  try {
    blob = await response.blob();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[fetchAPIBlob] failed to read blob from', url, error);
    throw new ApiErrorImpl('Failed to read download response', response.status);
  }

  // Parse filename from Content-Disposition: attachment; filename="foo.tar.gz"
  const disposition = response.headers.get('Content-Disposition') ?? '';
  const filenameMatch = /filename[^;=\n]*=(['"]?)([^'";\n]+)\1/.exec(disposition);
  const filename = filenameMatch ? filenameMatch[2].trim() : fallbackFilename;

  return { blob, filename };
}

/**
 * System Statistics API
 */
export const systemAPI = {
  /**
   * Get system statistics (CPU, memory, disk, containers).
   *
   * @returns System statistics
   */
  getStats: (): Promise<SystemStats> => {
    return fetchAPI<SystemStats>(`${API_BASE_URL}/admin/stats`);
  },
};

/**
 * User Management API
 */
export const usersAPI = {
  /**
   * List a PAGE of users.
   *
   * `/api/admin/users` is keyset-paginated: `cursor` + `limit`, default page
   * size 20, ordered `id DESC` (admin_routes.py). This wrapper used to send
   * neither parameter and callers ignored `next_cursor`, so the admin table
   * silently showed only the 20 most-recently-created accounts — and because
   * the order is `id DESC`, the ones it hid were the OLDEST, i.e. the
   * operator's own account and their earliest customers.
   *
   * The name is now honest about returning one page. Callers must follow
   * `next_cursor` (see `UserTable`'s Load more) rather than assuming a single
   * response is the whole set.
   *
   * @param cursor  return rows with id < cursor; omit for the first page
   * @param limit   page size, 1–100 (server clamps; server default is 20)
   */
  list: (opts: { cursor?: number | null; limit?: number } = {}): Promise<UserListResponse> => {
    const params = new URLSearchParams();
    if (opts.cursor != null) params.set('cursor', String(opts.cursor));
    if (opts.limit != null) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return fetchAPI<UserListResponse>(
      `${API_BASE_URL}/admin/users${qs ? `?${qs}` : ''}`,
    );
  },

  /**
   * Every user, by following `next_cursor` to exhaustion.
   *
   * For callers that need a COMPLETE set rather than a page — a picker, a
   * selector, an export. `UserTable` deliberately does not use this: paging on
   * demand is right for a browsable list, and eagerly pulling thousands of
   * rows to render twenty is not.
   *
   * Exists so the paging logic lives in ONE place. Both callers of `list()`
   * had independently assumed a single response was the whole set, which is
   * the "every call site, not one" failure CLAUDE.md §11 describes: the fix
   * belongs where it cannot be forgotten, not repeated per consumer.
   *
   * `maxPages` is a runaway guard, not a limit anyone should hit; at the
   * server's 100-row ceiling it covers 10,000 accounts.
   */
  listAll: async (maxPages = 100): Promise<User[]> => {
    const all: User[] = [];
    let cursor: number | null | undefined = undefined;
    for (let page = 0; page < maxPages; page++) {
      const res: UserListResponse = await usersAPI.list({ cursor, limit: 100 });
      all.push(...(res.rows ?? []));
      cursor = res.next_cursor;
      if (cursor == null) return all;
    }
    // Truncation is a real (if unlikely) outcome — say so rather than
    // returning a silently-short list, which is the exact shape of the bug
    // this helper exists to fix.
    console.error(
      `[usersAPI.listAll] stopped after ${maxPages} pages; result is truncated`,
    );
    return all;
  },

  /**
   * Create a new user.
   *
   * @param data - User creation request
   * @returns Created user details
   */
  create: (data: UserCreateRequest): Promise<User> => {
    return fetchAPI<User>(`${API_BASE_URL}/admin/users`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Suspend or reactivate a user (toggles active status).
   *
   * @param userId - User ID
   * @returns Updated user details
   */
  suspend: (userId: number): Promise<User> => {
    return fetchAPI<User>(`${API_BASE_URL}/admin/users/${userId}/suspend`, {
      method: 'PUT',
    });
  },

  /**
   * Delete a user permanently.
   *
   * @param userId - User ID
   * @returns void (204 No Content)
   */
  delete: (userId: number): Promise<void> => {
    return fetchAPI<void>(`${API_BASE_URL}/admin/users/${userId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Terminate a user account in full — stop+destroy containers,
   * delete domains/DNS/certs/sessions/snapshots/crons/backups, wipe
   * on-disk home directory, remove host /etc/passwd entry, then drop
   * the User row. Distinct from `delete` (containers only) and from
   * the GDPR erasure flow (tenant-consent + audit anonymization).
   *
   * @param userId - User ID
   * @returns void (204 No Content)
   */
  terminate: (userId: number): Promise<void> => {
    return fetchAPI<void>(`${API_BASE_URL}/admin/users/${userId}/terminate`, {
      method: 'POST',
    });
  },

  /**
   * Admin-set a user's password without knowing the current one.
   *
   * If `new_password` is omitted, the server generates a random one and
   * returns it in `new_password` so the admin can hand it off once.
   *
   * @param userId - User ID
   * @param data - Optional explicit new password
   * @returns Result with id/username/message and (when generated) the new password
   */
  resetPassword: (
    userId: number,
    data: AdminPasswordResetRequest = {},
  ): Promise<AdminPasswordResetResponse> => {
    return fetchAPI<AdminPasswordResetResponse>(
      `${API_BASE_URL}/admin/users/${userId}/password`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    );
  },
};

/**
 * Global Settings API
 */
export const settingsAPI = {
  /**
   * Get global settings (hostname, nameservers, default limits).
   *
   * @returns Global settings
   */
  get: (): Promise<GlobalSettings> => {
    return fetchAPI<GlobalSettings>(`${API_BASE_URL}/admin/settings`);
  },

  /**
   * Update global settings (partial update).
   *
   * @param data - Settings update request (only provided fields are updated)
   * @returns Updated global settings
   */
  update: (data: GlobalSettingsUpdateRequest): Promise<GlobalSettings> => {
    return fetchAPI<GlobalSettings>(`${API_BASE_URL}/admin/settings`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

/**
 * Authentication API
 */
export const authAPI = {
  /** Whether recovery email is configured, so "Forgot your password?" can work. */
  passwordResetAvailability: (): Promise<{ available: boolean }> =>
    fetchAPI(`${API_BASE_URL}/auth/password-reset/availability`, { suppressAuthRedirect: true }),
  requestPasswordReset: (email: string): Promise<{ message: string }> =>
    fetchAPI(`${API_BASE_URL}/auth/password-reset/request`, {
      method: 'POST', body: JSON.stringify({ email }), suppressAuthRedirect: true,
    }),
  /** Whether public self-registration is open (unauthenticated). */
  registrationStatus: (): Promise<{ enabled: boolean }> =>
    fetchAPI(`${API_BASE_URL}/auth/registration`, { suppressAuthRedirect: true }),
  confirmPasswordReset: (token: string, new_password: string): Promise<{ message: string }> =>
    fetchAPI(`${API_BASE_URL}/auth/password-reset/confirm`, {
      method: 'POST', body: JSON.stringify({ token, new_password }), suppressAuthRedirect: true,
    }),
  /**
   * Login with username and password.
   *
   * @param data - Login credentials
   * @returns Token response with access token
   */
  login: (data: LoginRequest): Promise<TokenResponse> => {
    return fetchAPI<TokenResponse>(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Fetch the currently authenticated user's profile.
   *
   * @returns Authenticated user details
   */
  /**
   * Change the signed-in user's own password (also clears a forced change).
   * The server re-issues the session cookie, so the caller stays signed in;
   * every other session is signed out.
   */
  changePassword: (currentPassword: string, newPassword: string): Promise<TokenResponse> => {
    return fetchAPI<TokenResponse>(`${API_BASE_URL}/auth/password/change`, {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  },

  me: (): Promise<AuthUser> => {
    // AMBIENT by definition: AuthContext calls this on every mount to find
    // out WHETHER there is a session. A 401 is the answer to that question,
    // not a session expiring mid-flow, and the caller already handles it by
    // setting `user = null` — after which ProtectedRoute redirects properly,
    // with the right target for the current bundle.
    //
    // Letting the shared handler fire here hard-navigated the browser out of
    // any page a signed-out user is allowed to be on. That is what made
    // `/register` unreachable, and what ejected the impersonation landing tab
    // (`/admin/impersonate?code=…`) to the login screen before it could
    // redeem its code — a tab which by design starts with NO session, since
    // establishing one is its entire purpose.
    return fetchAPI<AuthUser>(`${API_BASE_URL}/auth/me`, {
      suppressAuthRedirect: true,
    });
  },

  /**
   * Register a new user account.
   *
   * @param data - Registration details
   * @returns Created user details
   */
  register: (data: RegisterRequest): Promise<AuthUser> => {
    return fetchAPI<AuthUser>(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Logout and invalidate current session.
   *
   * @returns void (204 No Content)
   */
  logout: (): Promise<void> => {
    return fetchAPI<void>(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
    });
  },

  /**
   * Setup two-factor authentication (generate TOTP secret and QR code).
   *
   * @returns TOTP secret and QR code URL
   */
  setup2FA: (): Promise<TwoFactorSetupResponse> => {
    return fetchAPI<TwoFactorSetupResponse>(`${API_BASE_URL}/auth/2fa/setup`, {
      method: 'POST',
    });
  },

  /**
   * Verify TOTP token and enable two-factor authentication.
   *
   * @param data - Verification request with 6-digit TOTP token
   * @returns Verification result
   */
  verify2FA: (data: TwoFactorVerifyRequest): Promise<TwoFactorVerifyResponse> => {
    return fetchAPI<TwoFactorVerifyResponse>(`${API_BASE_URL}/auth/2fa/verify`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Disable two-factor authentication.
   *
   * @param data - Disable request with password for verification
   * @returns Disable result
   */
  disable2FA: (data: TwoFactorDisableRequest): Promise<TwoFactorDisableResponse> => {
    return fetchAPI<TwoFactorDisableResponse>(`${API_BASE_URL}/auth/2fa/disable`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

/**
 * Container Management API
 */
export const containersAPI = {
  /**
   * List all containers.
   *
   * @returns List of containers with total count
   */
  list: (): Promise<ContainerListResponse> => {
    return fetchAPI<ContainerListResponse>(`${API_BASE_URL}/containers`);
  },

  /**
   * Get details of a specific container.
   *
   * @param containerId - Container ID
   * @returns Container details
   */
  get: (containerId: number): Promise<Container> => {
    return fetchAPI<Container>(`${API_BASE_URL}/containers/${containerId}`);
  },

  /**
   * Create a new container.
   *
   * @param data - Container creation request
   * @returns Created container details
   */
  create: (data: ContainerCreateRequest): Promise<Container> => {
    return fetchAPI<Container>(`${API_BASE_URL}/containers`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Start a container.
   *
   * @param containerId - Container ID
   * @returns Action response with status and message
   */
  start: (containerId: number): Promise<ContainerActionResponse> => {
    return fetchAPI<ContainerActionResponse>(
      `${API_BASE_URL}/containers/${containerId}/start`,
      {
        method: 'POST',
      }
    );
  },

  /**
   * Stop a container.
   *
   * @param containerId - Container ID
   * @returns Action response with status and message
   */
  stop: (containerId: number): Promise<ContainerActionResponse> => {
    return fetchAPI<ContainerActionResponse>(
      `${API_BASE_URL}/containers/${containerId}/stop`,
      {
        method: 'POST',
      }
    );
  },

  /**
   * Restart a container.
   *
   * @param containerId - Container ID
   * @returns Action response with status and message
   */
  restart: (containerId: number): Promise<ContainerActionResponse> => {
    return fetchAPI<ContainerActionResponse>(
      `${API_BASE_URL}/containers/${containerId}/restart`,
      {
        method: 'POST',
      }
    );
  },

  /**
   * Delete a container.
   *
   * @param containerId - Container ID
   * @returns void (204 No Content)
   */
  delete: (containerId: number): Promise<void> => {
    return fetchAPI<void>(`${API_BASE_URL}/containers/${containerId}`, {
      method: 'DELETE',
    });
  },
};

/**
/**
 * Client Dashboard Overview API
 */
export const clientAPI = {
  /**
   * Get client dashboard overview (domain count, resource usage).
   *
   * @param userId - User ID (defaults to 1 for MVP)
   * @returns Client overview data
   */
  overview: (userId: number = 1): Promise<ClientOverview> => {
    return fetchAPI<ClientOverview>(`${API_BASE_URL}/client/overview?user_id=${userId}`);
  },
};

/**
 * Domains Management API
 */
export const domainsAPI = {
  /**
   * List all domains.
   *
   * @returns List of domains with total count
   */
  list: (userId?: number): Promise<DomainListResponse> => {
    const url = userId ? `${API_BASE_URL}/client/domains?user_id=${userId}` : `${API_BASE_URL}/domains`;
    return fetchAPI<DomainListResponse>(url);
  },

  /**
   * Create a new domain.
   *
   * @param data - Domain creation request
   * @returns Created domain details
   */
  create: (data: DomainCreateRequest, userId?: number): Promise<Domain> => {
    const url = userId ? `${API_BASE_URL}/client/domains?user_id=${userId}` : `${API_BASE_URL}/domains`;
    return fetchAPI<Domain>(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  createSubdomain: (domainId: number, data: DomainCreateRequest): Promise<Domain> => {
    return fetchAPI<Domain>(`${API_BASE_URL}/domains/${domainId}/subdomains`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get details of a specific domain.
   *
   * @param domainId - Domain ID
   * @returns Domain details
   */
  get: (domainId: number, userId: number = 1): Promise<Domain> => {
    return fetchAPI<Domain>(`${API_BASE_URL}/client/domains/${domainId}?user_id=${userId}`);
  },

  updateStatus: (
    domainId: number,
    status: 'active' | 'suspended' | 'pending_dns',
  ): Promise<DomainActionResponse> => {
    return fetchAPI<DomainActionResponse>(
      `${API_BASE_URL}/domains/${domainId}/status/${status}`,
      { method: 'PUT' },
    );
  },

  delete: (
    domainId: number,
    data: DomainDeleteRequest = {},
  ): Promise<DomainActionResponse> => {
    return fetchAPI<DomainActionResponse>(
      `${API_BASE_URL}/domains/${domainId}`,
      { method: 'DELETE', body: JSON.stringify(data) },
    );
  },

  /**
   * Read recent stdout+stderr from the container backing this domain.
   * Hits the JWT-authenticated tenant route at /api/v2/client/domains/{id}/logs;
   * the API_BASE_URL prefix is `/api`, so we prepend `/v2` here.
   */
  getLogs: (
    domainId: number,
    tail: number = 200,
  ): Promise<import('./types').DomainLogsResponse> => {
    const clamped = Math.max(1, Math.min(2000, Math.floor(tail)));
    return fetchAPI<import('./types').DomainLogsResponse>(
      `${API_BASE_URL}/v2/client/domains/${domainId}/logs?tail=${clamped}`,
    );
  },
};

/**
 * Per-container PHP overrides — tenant edits the per-domain (= per-
 * container) file; admin endpoints touch the operator-wide global
 * file or any tenant's file. The tenant body layers on top of the
 * global; PHP loads them in conf.d/ alphabetical order.
 */
export const phpIniAPI = {
  getTenant: (domainId: number) =>
    fetchAPI<import('./types').TenantPhpIniResponse>(
      `${API_BASE_URL}/php-ini/domains/${domainId}`,
    ),
  updateTenant: (domainId: number, body: string) =>
    fetchAPI<import('./types').TenantPhpIniResponse>(
      `${API_BASE_URL}/php-ini/domains/${domainId}`,
      { method: 'PUT', body: JSON.stringify({ body }) },
    ),
  getGlobal: () =>
    fetchAPI<import('./types').GlobalPhpIniResponse>(
      `${API_BASE_URL}/admin/php-ini/global`,
    ),
  updateGlobal: (body: string) =>
    fetchAPI<import('./types').PhpIniRestartResult>(
      `${API_BASE_URL}/admin/php-ini/global`,
      { method: 'PUT', body: JSON.stringify({ body }) },
    ),
  getForUser: (userId: number) =>
    fetchAPI<import('./types').GlobalPhpIniResponse>(
      `${API_BASE_URL}/admin/php-ini/users/${userId}`,
    ),
  updateForUser: (userId: number, body: string) =>
    fetchAPI<import('./types').PhpIniRestartResult>(
      `${API_BASE_URL}/admin/php-ini/users/${userId}`,
      { method: 'PUT', body: JSON.stringify({ body }) },
    ),
};

/**
 * Client Databases API.
 *
 * Wired to the W1-B locked contract under `/api/client/databases` —
 * see `apps/client/api/routes/db.py`. Every call goes through
 * `fetchAPI`, which means:
 *   - No raw fetch errors leak to the user (CLAUDE.md §9).
 *   - 401/403 redirect to /login automatically.
 *   - 503 surfaces with `.status === 503` so the page can render the
 *     §9 "service unavailable" banner instead of a generic toast.
 *
 * The legacy `?user_id=` query param is gone — auth + ownership
 * scoping is enforced server-side via the JWT.
 */
export const clientDatabasesAPI = {
  /** List all databases owned by the authenticated tenant. */
  list: (): Promise<ClientDatabaseListResponse> => {
    return fetchAPI<ClientDatabaseListResponse>(`${API_BASE_URL}/client/databases`);
  },

  /** Provision a database + primary user. */
  create: (data: ClientDatabaseCreateRequest): Promise<ClientDatabase> => {
    return fetchAPI<ClientDatabase>(`${API_BASE_URL}/client/databases`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /** Detail view (database + users). */
  getOne: (dbId: number): Promise<ClientDatabaseDetailResponse> => {
    return fetchAPI<ClientDatabaseDetailResponse>(
      `${API_BASE_URL}/client/databases/${dbId}`,
    );
  },

  /** Cascade-drop the database and all its users. */
  delete: (dbId: number): Promise<ClientDatabaseDeleteResponse> => {
    return fetchAPI<ClientDatabaseDeleteResponse>(
      `${API_BASE_URL}/client/databases/${dbId}`,
      { method: 'DELETE' },
    );
  },

  /** Rotate the primary user's password. */
  rotatePrimaryPassword: (
    dbId: number,
    body: DbPasswordRotateRequest,
  ): Promise<ClientDatabaseUser> => {
    return fetchAPI<ClientDatabaseUser>(
      `${API_BASE_URL}/client/databases/${dbId}/password`,
      { method: 'PUT', body: JSON.stringify(body) },
    );
  },

  /** List the users attached to a database. */
  listUsers: (dbId: number): Promise<ClientDatabaseUserListResponse> => {
    return fetchAPI<ClientDatabaseUserListResponse>(
      `${API_BASE_URL}/client/databases/${dbId}/users`,
    );
  },

  /** Add a DB user (username_suffix joined to tenant prefix server-side). */
  addUser: (
    dbId: number,
    body: ClientDatabaseUserCreateRequest,
  ): Promise<ClientDatabaseUser> => {
    return fetchAPI<ClientDatabaseUser>(
      `${API_BASE_URL}/client/databases/${dbId}/users`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  },

  /** REVOKE all grants and DROP USER for a non-primary DB user. */
  removeUser: (
    dbId: number,
    dbUserId: number,
  ): Promise<ClientDatabaseUser> => {
    return fetchAPI<ClientDatabaseUser>(
      `${API_BASE_URL}/client/databases/${dbId}/users/${dbUserId}`,
      { method: 'DELETE' },
    );
  },

  /** Change a DB user's permission level (revoke-all + grant-new). */
  updateUserPermissions: (
    dbId: number,
    dbUserId: number,
    body: ClientDatabaseUserUpdatePermissionsRequest,
  ): Promise<ClientDatabaseUser> => {
    return fetchAPI<ClientDatabaseUser>(
      `${API_BASE_URL}/client/databases/${dbId}/users/${dbUserId}/permissions`,
      { method: 'PUT', body: JSON.stringify(body) },
    );
  },

  /** Rotate a non-primary DB user's password. */
  rotateUserPassword: (
    dbId: number,
    dbUserId: number,
    body: DbPasswordRotateRequest,
  ): Promise<ClientDatabaseUser> => {
    return fetchAPI<ClientDatabaseUser>(
      `${API_BASE_URL}/client/databases/${dbId}/users/${dbUserId}/password`,
      { method: 'PUT', body: JSON.stringify(body) },
    );
  },

  /**
   * Mint a one-shot phpMyAdmin SSO URL.
   *
   * The current backend route accepts only `db_id` (the broker uses
   * the primary user). `dbUserId` is reserved here for the eventual
   * "open phpMyAdmin as user X" feature; it's currently sent as a
   * query param so the route signature can grow without breaking the
   * frontend, and is silently ignored if the backend doesn't read it.
   */
  mintPmaLink: (
    dbId: number,
    dbUserId?: number,
  ): Promise<PmaSsoLinkResponse> => {
    const q = dbUserId == null ? '' : `?user_id=${dbUserId}`;
    return fetchAPI<PmaSsoLinkResponse>(
      `${API_BASE_URL}/client/databases/${dbId}/pma-link${q}`,
      { method: 'POST' },
    );
  },
};

/**
 * Admin proxy (Caddy) operations.
 *
 * - `previewConfig`  GET  /api/admin/proxy/config   — rendered Caddyfile text
 * - `reload`         POST /api/admin/proxy/reload   — graceful reload (no-op if unchanged)
 * - `restart`        POST /api/admin/proxy/restart  — hard restart of the host Caddy container
 *
 * The admin SPA is also served by the FastAPI process on :8001 (not via
 * Caddy), so these operations remain reachable even when Caddy is dead —
 * `restart` is the break-glass recovery path.
 */
export interface ProxyConfigResponse {
  config: string
  entry_count: number
}
export interface ProxyReloadResponse {
  reloaded: boolean
  message: string
}
export interface ProxyRestartResponse {
  restarted: boolean
  container: string
  message: string
}
export interface ProxyHealthResponse {
  caddyfile_path: string
  caddyfile_exists: boolean
  caddyfile_size_bytes: number
}

/**
 * Recovery-port status (GET /api/admin/recovery/status). `reasons` codes:
 * "disabled" | "no_panel_hostname" | "cert_missing" | "allowlist_empty".
 */
export interface RecoveryStatusResponse {
  enabled: boolean
  port: number | null
  armed: boolean
  reasons: string[]
  url: string | null
  allowlist_count: number
}

export const proxyAPI = {
  previewConfig: (): Promise<ProxyConfigResponse> =>
    fetchAPI<ProxyConfigResponse>(`${API_BASE_URL}/admin/proxy/config`),
  reload: (): Promise<ProxyReloadResponse> =>
    fetchAPI<ProxyReloadResponse>(`${API_BASE_URL}/admin/proxy/reload`, {
      method: 'POST',
    }),
  restart: (): Promise<ProxyRestartResponse> =>
    fetchAPI<ProxyRestartResponse>(`${API_BASE_URL}/admin/proxy/restart`, {
      method: 'POST',
    }),
  health: (): Promise<ProxyHealthResponse> =>
    fetchAPI<ProxyHealthResponse>(`${API_BASE_URL}/admin/proxy/health`),
}

export const getRecoveryStatus = (): Promise<RecoveryStatusResponse> =>
  fetchAPI<RecoveryStatusResponse>(`${API_BASE_URL}/admin/recovery/status`)

/**
 * SSL Certificates API (tenant-scoped).
 *
 * `list` and `renew` go through the User Control Plane endpoints which
 * verify ownership via the cert's domain. `userId` is required because
 * the existing client endpoints take it as a query param.
 */
export const certificatesAPI = {
  list: (userId: number): Promise<CertificateListResponse> => {
    return fetchAPI<CertificateListResponse>(
      `${API_BASE_URL}/client/certificates?user_id=${userId}`
    );
  },

  renew: (certificateId: number, userId: number): Promise<Certificate> => {
    return fetchAPI<Certificate>(
      `${API_BASE_URL}/client/certificates/${certificateId}/renew?user_id=${userId}`,
      { method: 'POST' }
    );
  },

  /** Issue a certificate for a domain the caller owns. */
  issue: (domainId: number): Promise<Certificate> => {
    return fetchAPI<Certificate>(`${API_BASE_URL}/certificates/issue`, {
      method: 'POST',
      body: JSON.stringify({ domain_id: domainId }),
    });
  },
};

/** Server identity + per-domain DNS check (tenant-scoped). */
export const serverAPI = {
  info: (): Promise<import('./types').ServerInfo> =>
    fetchAPI(`${API_BASE_URL}/client/server-info`),
  dnsCheck: (domainId: number): Promise<import('./types').DomainDnsCheck> =>
    fetchAPI(`${API_BASE_URL}/client/domains/${domainId}/dns-check`),
};

/** Admin: whether disk quotas are actually enforced on this host. */
export const quotaStatusAPI = {
  get: (): Promise<{ quota_enforced: boolean; quota_reason: string | null; backend: string }> =>
    fetchAPI(`${API_BASE_URL}/admin/quotas/status`),
};

/** Tenant-visible mail service health. */
export const mailHealthAPI = {
  get: (): Promise<import('./types').MailServiceHealth> =>
    fetchAPI(`${API_BASE_URL}/email/health`),
};

/**
 * Plan 4: Runtime image catalog + per-tenant runtime swap.
 */
export interface BackendAvailability {
  kind: 'docker' | 'lxc';
  available: boolean;
  /** Operator-actionable explanation when `available` is false. */
  reason: string | null;
}

export const runtimeAPI = {
  /**
   * GET /api/runtime/backends — which container backends this server can
   * actually run. Consumed by the Add Domain wizard so it stops offering a
   * runtime that will fail (bug #7).
   */
  listBackends: (): Promise<BackendAvailability[]> =>
    fetchAPI<BackendAvailability[]>(`${API_BASE_URL}/runtime/backends`),

  listAvailable: (
    backendKind?: 'docker' | 'lxc'
  ): Promise<import('./types').CatalogImage[]> => {
    const q = backendKind ? `?backend_kind=${backendKind}` : '';
    return fetchAPI(`${API_BASE_URL}/runtime/images/available${q}`);
  },
  getCurrent: (
    containerId: number
  ): Promise<import('./types').CurrentRuntime> => {
    return fetchAPI(`${API_BASE_URL}/runtime/${containerId}`);
  },
  swap: (
    containerId: number,
    body: import('./types').RuntimeSwapRequest
  ): Promise<import('./types').RuntimeSwapResult> => {
    return fetchAPI(`${API_BASE_URL}/runtime/${containerId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
};

/**
 * Plan 8 v1: Hosting packages (admin CRUD + assign).
 */
export interface AdminAccess {
  allowed_cidrs: string[];
  your_ip: string;
}

export interface BackupVerification {
  id: number;
  started_at: string | null;
  finished_at: string | null;
  ok: boolean;
  subset: string;
  snapshot_id: string | null;
  check_summary: string | null;
  error: string | null;
}

/** Off-site (Restic) backup repository: status and verification. */
export const offsiteBackupsAPI = {
  status: (): Promise<{ enabled: boolean; repository: string | null }> =>
    fetchAPI(`${API_BASE_URL}/admin/backups/remote/status`),
  verify: (subset = '5%'): Promise<BackupVerification> =>
    fetchAPI<BackupVerification>(`${API_BASE_URL}/admin/backups/remote/verify`, {
      method: 'POST',
      body: JSON.stringify({ subset }),
    }),
  verifications: (): Promise<BackupVerification[]> =>
    fetchAPI<BackupVerification[]>(`${API_BASE_URL}/admin/backups/remote/verifications`),
};

/** Admin IP allow-list (empty = any address). */
export const adminAccessAPI = {
  get: (): Promise<AdminAccess> => fetchAPI<AdminAccess>(`${API_BASE_URL}/admin/security/admin-access`),
  set: (allowed_cidrs: string[]): Promise<AdminAccess> =>
    fetchAPI<AdminAccess>(`${API_BASE_URL}/admin/security/admin-access`, {
      method: 'PUT',
      body: JSON.stringify({ allowed_cidrs }),
    }),
};

export interface LoginBlock { account: string; failing_ips: number; since: string | null }

/** Accounts blocked by the login lockout (>5 failing addresses in 24 h). */
export const loginBlocksAPI = {
  list: (): Promise<{ blocks: LoginBlock[] }> =>
    fetchAPI<{ blocks: LoginBlock[] }>(`${API_BASE_URL}/admin/security/login-blocks`),
  clear: (account: string): Promise<{ cleared: string }> =>
    fetchAPI<{ cleared: string }>(
      `${API_BASE_URL}/admin/security/login-blocks/${encodeURIComponent(account)}`,
      { method: 'DELETE' },
    ),
};

export const packagesAPI = {
  list: (
    onlyActive = false
  ): Promise<import('./types').Package[]> => {
    const q = onlyActive ? '?only_active=true' : '';
    return fetchAPI(`${API_BASE_URL}/admin/packages${q}`);
  },
  get: (id: number): Promise<import('./types').Package> => {
    return fetchAPI(`${API_BASE_URL}/admin/packages/${id}`);
  },
  create: (
    body: import('./types').PackageCreateRequest
  ): Promise<import('./types').Package> => {
    return fetchAPI(`${API_BASE_URL}/admin/packages`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  update: (
    id: number,
    body: import('./types').PackageUpdateRequest
  ): Promise<import('./types').Package> => {
    return fetchAPI(`${API_BASE_URL}/admin/packages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
  delete: (id: number): Promise<void> => {
    return fetchAPI(`${API_BASE_URL}/admin/packages/${id}`, {
      method: 'DELETE',
    });
  },
  assign: (
    userId: number,
    packageId: number | null
  ): Promise<unknown> => {
    return fetchAPI(`${API_BASE_URL}/admin/packages/assign/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ package_id: packageId }),
    });
  },
};

/**
 * Plan 8 v2: Resellers + cascade-suspend.
 */
export const resellersAPI = {
  tree: (userId: number): Promise<unknown[]> => {
    return fetchAPI(`${API_BASE_URL}/admin/resellers/${userId}/tree`);
  },
  quotaUsage: (
    userId: number
  ): Promise<import('./types').ResellerQuotaUsage> => {
    return fetchAPI(`${API_BASE_URL}/admin/resellers/${userId}/quota-usage`);
  },
  grantQuota: (
    userId: number,
    body: import('./types').QuotaGrantRequest
  ): Promise<unknown> => {
    return fetchAPI(`${API_BASE_URL}/admin/resellers/${userId}/quota`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
  setParent: (userId: number, parentId: number | null): Promise<unknown> => {
    return fetchAPI(`${API_BASE_URL}/admin/users/${userId}/parent`, {
      method: 'PUT',
      body: JSON.stringify({ parent_id: parentId }),
    });
  },
  suspend: (userId: number, cascade = false): Promise<unknown> => {
    return fetchAPI(
      `${API_BASE_URL}/admin/users/${userId}/suspend?cascade=${cascade}`,
      { method: 'POST' }
    );
  },
  reactivate: (userId: number, cascade = false): Promise<unknown> => {
    return fetchAPI(
      `${API_BASE_URL}/admin/users/${userId}/reactivate?cascade=${cascade}`,
      { method: 'POST' }
    );
  },
};

/**
 * Plan 8 v2: White-label branding (tenant self-serve + admin override).
 */
export const whiteLabelAPI = {
  getMine: (): Promise<import('./types').WhiteLabel | Record<string, never>> => {
    return fetchAPI(`${API_BASE_URL}/white-label/me`);
  },
  upsertMine: (
    body: Partial<import('./types').WhiteLabel>
  ): Promise<import('./types').WhiteLabel> => {
    return fetchAPI(`${API_BASE_URL}/white-label/me`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
  deleteMine: (): Promise<void> => {
    return fetchAPI(`${API_BASE_URL}/white-label/me`, { method: 'DELETE' });
  },
};

/**
 * Plan 9: GDPR export + erasure.
 */
export const gdprAPI = {
  requestExport: (): Promise<import('./types').GdprExport> => {
    return fetchAPI(`${API_BASE_URL}/gdpr/exports`, { method: 'POST' });
  },
  getExport: (id: number): Promise<import('./types').GdprExport> => {
    return fetchAPI(`${API_BASE_URL}/gdpr/exports/${id}`);
  },
  /**
   * F08-011: download via fetchAPIBlob + custom header (instead of
   * `<a href="?token=...">`) so the bearer-equivalent token doesn't
   * leak through the URL into the browser history, Referer, or proxy
   * logs. The caller wires this into the standard
   * `triggerDownload(blob, filename)` flow.
   */
  download: async (
    id: number,
    token: string,
    fallbackFilename = `hpanel-export-${id}.zip`,
  ): Promise<{ blob: Blob; filename: string }> => {
    return fetchAPIBlob(
      `${API_BASE_URL}/gdpr/exports/${id}/download`,
      fallbackFilename,
      { 'X-Gdpr-Download-Token': token },
    );
  },
  requestErasure: (): Promise<import('./types').GdprErasure> => {
    return fetchAPI(`${API_BASE_URL}/gdpr/erasure`, {
      method: 'POST',
      body: JSON.stringify({ confirm: true }),
    });
  },
  // Admin
  listPendingErasures: (): Promise<import('./types').GdprErasure[]> => {
    return fetchAPI(`${API_BASE_URL}/admin/gdpr/erasure`);
  },
  approveErasure: (id: number): Promise<import('./types').GdprErasure> => {
    return fetchAPI(`${API_BASE_URL}/admin/gdpr/erasure/${id}/approve`, {
      method: 'POST',
    });
  },
  rejectErasure: (
    id: number,
    reason: string
  ): Promise<import('./types').GdprErasure> => {
    return fetchAPI(`${API_BASE_URL}/admin/gdpr/erasure/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },
};

/**
 * Plan 10: Per-tenant metrics + admin fleet.
 */
export const metricsAPI = {
  getMine: (): Promise<import('./types').TenantSnapshot[]> => {
    return fetchAPI(`${API_BASE_URL}/metrics/me`);
  },
  fleet: (): Promise<import('./types').TenantSnapshot[]> => {
    return fetchAPI(`${API_BASE_URL}/metrics/admin/fleet`);
  },
};

/**
 * Plan 3: DNS records (per-domain editor).
 */
export const dnsAPI = {
  publication: (domainId: number): Promise<import('./types').DnsPublication> =>
    fetchAPI(`${API_BASE_URL}/dns/${domainId}/publication`),
  publish: (domainId: number): Promise<import('./types').DnsPublication> =>
    fetchAPI(`${API_BASE_URL}/dns/${domainId}/publish`, { method: 'POST' }),
  delegation: (domainId: number): Promise<import('./types').DnsDelegation> =>
    fetchAPI(`${API_BASE_URL}/dns/${domainId}/delegation`),
  checkDelegation: (domainId: number): Promise<import('./types').DnsDelegation> =>
    fetchAPI(`${API_BASE_URL}/dns/${domainId}/delegation/check`, { method: 'POST' }),
  verifyEmail: (domainId: number): Promise<import('./types').EmailVerification> =>
    fetchAPI(`${API_BASE_URL}/dns/${domainId}/email-verification`),
  list: (
    domainId: number
  ): Promise<import('./types').DnsRecord[]> => {
    return fetchAPI(`${API_BASE_URL}/dns/${domainId}`);
  },
  create: (
    domainId: number,
    body: import('./types').DnsRecordCreateRequest
  ): Promise<import('./types').DnsRecord> => {
    return fetchAPI(`${API_BASE_URL}/dns/${domainId}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  delete: (domainId: number, recordId: number): Promise<void> => {
    return fetchAPI(`${API_BASE_URL}/dns/${domainId}/${recordId}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Admin › DNS server: the bundled PowerDNS server, nameserver hostnames and
 * per-domain zone switches.
 */
export const adminDnsAPI = {
  status: (): Promise<import('./types').DnsServerStatus> =>
    fetchAPI(`${API_BASE_URL}/admin/dns/server`),
  start: (): Promise<Record<string, unknown>> =>
    fetchAPI(`${API_BASE_URL}/admin/dns/server/start`, { method: 'POST' }),
  stop: (): Promise<Record<string, unknown>> =>
    fetchAPI(`${API_BASE_URL}/admin/dns/server/stop`, { method: 'POST' }),
  setNameservers: (
    body: import('./types').DnsNameserversUpdate
  ): Promise<import('./types').DnsNameserversResult> =>
    fetchAPI(`${API_BASE_URL}/admin/dns/nameservers`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  zones: (): Promise<import('./types').DnsZoneRow[]> =>
    fetchAPI(`${API_BASE_URL}/admin/dns/zones`),
  enableZone: (domainId: number): Promise<import('./types').DnsZoneRow> =>
    fetchAPI(`${API_BASE_URL}/admin/dns/zones/${domainId}/enable`, { method: 'POST' }),
  disableZone: (domainId: number): Promise<import('./types').DnsZoneRow> =>
    fetchAPI(`${API_BASE_URL}/admin/dns/zones/${domainId}/disable`, { method: 'POST' }),
};

/**
 * Plan 6: WordPress installs (tenant + admin fleet).
 */
export const wordpressAPI = {
  listMine: (): Promise<import('./types').WordPressInstall[]> => {
    return fetchAPI(`${API_BASE_URL}/wordpress`);
  },
  get: (id: number): Promise<import('./types').WordPressInstall> => {
    return fetchAPI(`${API_BASE_URL}/wordpress/${id}`);
  },
  install: (
    body: import('./types').WpInstallRequest
  ): Promise<import('./types').WordPressInstallWithCredentials> => {
    return fetchAPI(`${API_BASE_URL}/wordpress`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  resetAdminPassword: (
    id: number
  ): Promise<import('./types').WordPressInstallWithCredentials> => {
    return fetchAPI(`${API_BASE_URL}/wordpress/${id}/reset-admin-password`, {
      method: 'POST',
    });
  },
  delete: (id: number): Promise<void> => {
    return fetchAPI(`${API_BASE_URL}/wordpress/${id}`, {
      method: 'DELETE',
    });
  },
  update: (
    id: number,
    body: { core?: boolean; plugins?: boolean; themes?: boolean }
  ): Promise<unknown> => {
    return fetchAPI(`${API_BASE_URL}/wordpress/${id}/update`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  createStaging: (
    id: number,
    subdomain: string
  ): Promise<{
    reused: boolean;
    staging_url: string | null;
    staging: import('./types').WordPressInstall | null;
  }> => {
    return fetchAPI(`${API_BASE_URL}/wordpress/${id}/staging`, {
      method: 'POST',
      body: JSON.stringify({ subdomain }),
    });
  },
  fleet: (): Promise<import('./types').WordPressInstall[]> => {
    return fetchAPI(`${API_BASE_URL}/admin/wordpress`);
  },
};

/**
 * Plan 5b: Email — mailboxes + aliases + forwarders + DKIM.
 */
export const emailAPI = {
  listMailboxes: (): Promise<import('./types').Mailbox[]> => {
    return fetchAPI(`${API_BASE_URL}/email/mailboxes`);
  },
  createMailbox: (body: {
    domain_id: number;
    local_part: string;
    password: string;
    quota_mb?: number;
  }): Promise<import('./types').Mailbox> => {
    return fetchAPI(`${API_BASE_URL}/email/mailboxes`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  deleteMailbox: (id: number): Promise<void> => {
    return fetchAPI(`${API_BASE_URL}/email/mailboxes/${id}`, {
      method: 'DELETE',
    });
  },
  changeMailboxPassword: (id: number, password: string): Promise<unknown> => {
    return fetchAPI(`${API_BASE_URL}/email/mailboxes/${id}/password`, {
      method: 'PUT',
      body: JSON.stringify({ password }),
    });
  },
  listAliases: (): Promise<import('./types').MailAlias[]> => {
    return fetchAPI(`${API_BASE_URL}/email/aliases`);
  },
  createAlias: (body: {
    domain_id: number;
    source: string;
    destination: string;
  }): Promise<import('./types').MailAlias> => {
    return fetchAPI(`${API_BASE_URL}/email/aliases`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  deleteAlias: (id: number): Promise<void> => {
    return fetchAPI(`${API_BASE_URL}/email/aliases/${id}`, {
      method: 'DELETE',
    });
  },
  listForwarders: (): Promise<import('./types').MailForwarder[]> => {
    return fetchAPI(`${API_BASE_URL}/email/forwarders`);
  },
  createForwarder: (body: {
    domain_id: number;
    source: string;
    external: string;
  }): Promise<import('./types').MailForwarder> => {
    return fetchAPI(`${API_BASE_URL}/email/forwarders`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  deleteForwarder: (id: number): Promise<void> => {
    return fetchAPI(`${API_BASE_URL}/email/forwarders/${id}`, {
      method: 'DELETE',
    });
  },
  getDkim: (domainId: number): Promise<import('./types').DkimKey> => {
    return fetchAPI(`${API_BASE_URL}/email/dkim/${domainId}`);
  },
  rotateDkim: (
    domainId: number
  ): Promise<import('./types').DkimKey> => {
    return fetchAPI(`${API_BASE_URL}/admin/mail/dkim/${domainId}/rotate`, {
      method: 'POST',
    });
  },
  relayStatus: (): Promise<RelayStatus> => {
    return fetchAPI(`${API_BASE_URL}/email/relay/status`);
  },
};

export interface RelayStatus {
  sent_this_hour: number;
  rate_per_hour: number;
  enabled: boolean;
  relay_host_hint: string;
}

export interface EmailServiceState {
  status?: string;
  state?: string;
  uptime_s?: number;
  listen_ports?: string[];
  [k: string]: unknown;
}

export interface EmailStatusResponse {
  postfix: EmailServiceState;
  dovecot: EmailServiceState;
  roundcube: EmailServiceState;
}

export interface QueueEntry {
  queue_id: string;
  queue_name: string;
  sender: string;
  recipient_count: number;
  message_size: number;
  arrival_time: number;
}

export type EmailConfig = Record<string, string | number | boolean>;

export const adminEmailAPI = {
  status: (): Promise<EmailStatusResponse> =>
    fetchAPI(`${API_BASE_URL}/admin/email/status`),
  start: (svc: string): Promise<EmailStatusResponse> =>
    fetchAPI(`${API_BASE_URL}/admin/email/services/${svc}/start`, {
      method: 'POST',
    }),
  stop: (svc: string): Promise<EmailStatusResponse> =>
    fetchAPI(`${API_BASE_URL}/admin/email/services/${svc}/stop`, {
      method: 'POST',
    }),
  restart: (svc: string): Promise<EmailStatusResponse> =>
    fetchAPI(`${API_BASE_URL}/admin/email/services/${svc}/restart`, {
      method: 'POST',
    }),
  logs: (service: string, lines = 500): Promise<{ lines: string[] }> =>
    fetchAPI(
      `${API_BASE_URL}/admin/email/logs?service=${encodeURIComponent(
        service,
      )}&lines=${lines}`,
    ),
  queueList: (): Promise<QueueEntry[]> =>
    fetchAPI(`${API_BASE_URL}/admin/email/queue`),
  queueDelete: (id: string): Promise<void> =>
    fetchAPI(`${API_BASE_URL}/admin/email/queue/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  queueFlush: (id: string): Promise<void> =>
    fetchAPI(
      `${API_BASE_URL}/admin/email/queue/${encodeURIComponent(id)}/flush`,
      { method: 'POST' },
    ),
  queueFlushAll: (): Promise<{ flushed: number }> =>
    fetchAPI(`${API_BASE_URL}/admin/email/queue/flush-all`, { method: 'POST' }),
  configGet: (): Promise<EmailConfig> =>
    fetchAPI(`${API_BASE_URL}/admin/email/config`),
  configSet: (
    key: string,
    value: string | number | boolean,
  ): Promise<EmailConfig> =>
    fetchAPI(`${API_BASE_URL}/admin/email/config`, {
      method: 'PUT',
      body: JSON.stringify({ key, value }),
    }),
};

export const webmailAPI = {
  status: (): Promise<{ enabled: boolean; public_url: string | null }> => {
    return fetchAPI(`${API_BASE_URL}/email/webmail/status`);
  },
  launch: (
    mailboxId: number
  ): Promise<{ url: string; username: string }> => {
    return fetchAPI(
      `${API_BASE_URL}/email/webmail/launch?mailbox_id=${mailboxId}`
    );
  },
};

/**
 * Plan 4: Image catalog admin CRUD.
 */
export const imageCatalogAPI = {
  list: (): Promise<import('./types').CatalogImage[]> => {
    return fetchAPI(`${API_BASE_URL}/admin/images`);
  },
  create: (body: {
    name: string;
    oci_reference: string;
    backend_kind: 'docker' | 'lxc';
    runtime_kind: string;
    runtime_version: string;
    extensions?: string[];
    status?: 'default' | 'available' | 'deprecated';
  }): Promise<import('./types').CatalogImage> => {
    return fetchAPI(`${API_BASE_URL}/admin/images`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  update: (
    id: number,
    body: Partial<import('./types').CatalogImage>
  ): Promise<import('./types').CatalogImage> => {
    return fetchAPI(`${API_BASE_URL}/admin/images/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },
  delete: (id: number): Promise<void> => {
    return fetchAPI(`${API_BASE_URL}/admin/images/${id}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Plan 2: cPanel migration (admin).
 */
export const migrationAPI = {
  list: (): Promise<import('./types').CpanelImport[]> => {
    return fetchAPI(`${API_BASE_URL}/admin/migrations`);
  },
  get: (id: number): Promise<import('./types').CpanelImport> => {
    return fetchAPI(`${API_BASE_URL}/admin/migrations/${id}`);
  },
  start: (
    body: { path: string; username_remap?: string }
  ): Promise<import('./types').CpanelImport> => {
    return fetchAPI(`${API_BASE_URL}/admin/migrations/cpanel`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  resume: (id: number): Promise<import('./types').CpanelImport> => {
    return fetchAPI(`${API_BASE_URL}/admin/migrations/${id}/resume`, {
      method: 'POST',
    });
  },
  cancel: (id: number): Promise<void> => {
    return fetchAPI(`${API_BASE_URL}/admin/migrations/${id}`, {
      method: 'DELETE',
    });
  },
};

/**
 * Backups API (tenant-scoped).
 *
 * All endpoints filter by the authenticated user — tenants can only
 * see and act on their own backups. Download goes through fetchAPIBlob
 * (binary stream); everything else goes through fetchAPI (JSON).
 */
/** One entry in a tenant directory listing (`GET /api/files`). */
export interface FileEntry {
  name: string;
  /** "file" | "dir" — widened to string; the backend owns the vocabulary. */
  kind: string;
  size: number;
  /** Unix epoch seconds. */
  mtime: number;
}

/**
 * Tenant file manager. Paths are relative to the tenant's home root and are
 * always sent URL-encoded — names legitimately contain spaces, '#' and '+'.
 *
 * Uploads are JSON+base64 rather than multipart because that is what
 * `POST /api/files/upload` accepts (UploadRequest{path, content_b64}).
 */
/**
 * Normalise a UI path to what the API accepts.
 *
 * The file-manager UI models the tenant home as `/` and builds child paths by
 * appending — `/`, `/public`, `/public/index.php`. The API refuses a leading
 * slash outright ("path must be relative to the tenant home; absolute paths
 * refused"), so every call was rejected and the file manager failed to load
 * its own root directory. Translating here keeps the UI's natural absolute
 * model and satisfies the API contract in ONE place rather than at each of
 * the seven call sites below.
 */
const tenantRelative = (path: string): string => path.replace(/^\/+/, '');

export const filesAPI = {
  /** GET /api/files?path=… — directory listing. */
  list: (path: string): Promise<FileEntry[]> =>
    fetchAPI<FileEntry[]>(
      `${API_BASE_URL}/files?path=${encodeURIComponent(tenantRelative(path))}`,
    ),

  /** GET /api/files/read?path=… — text contents of a single file. */
  read: (path: string): Promise<{ path: string; content: string }> =>
    fetchAPI<{ path: string; content: string }>(
      `${API_BASE_URL}/files/read?path=${encodeURIComponent(tenantRelative(path))}`,
    ),

  /** PUT /api/files/write — overwrite a text file. */
  write: (path: string, content: string): Promise<unknown> =>
    fetchAPI<unknown>(`${API_BASE_URL}/files/write`, {
      method: 'PUT',
      body: JSON.stringify({ path: tenantRelative(path), content }),
    }),

  /** POST /api/files/upload — base64 payload, so it works for binaries too.
   *  Without `overwrite` the server answers 409 if the name already exists. */
  upload: (path: string, contentB64: string, overwrite = false): Promise<unknown> =>
    fetchAPI<unknown>(`${API_BASE_URL}/files/upload`, {
      method: 'POST',
      body: JSON.stringify({
        path: tenantRelative(path),
        content_b64: contentB64,
        overwrite,
      }),
    }),

  /** POST /api/files/extract — unzip a .zip into its own folder (never overwrites). */
  extract: (
    path: string,
  ): Promise<{ extracted: number; bytes: number; skipped: { name: string; reason: string }[] }> =>
    fetchAPI<{ extracted: number; bytes: number; skipped: { name: string; reason: string }[] }>(`${API_BASE_URL}/files/extract`, {
      method: 'POST',
      body: JSON.stringify({ path: tenantRelative(path) }),
    }),

  /** DELETE /api/files?path=… */
  remove: (path: string): Promise<unknown> =>
    fetchAPI<unknown>(
      `${API_BASE_URL}/files?path=${encodeURIComponent(tenantRelative(path))}`,
      { method: 'DELETE' },
    ),

  /** GET /api/files/download?path=… — binary-safe, so it uses the blob path. */
  download: (path: string): Promise<{ blob: Blob; filename: string }> =>
    fetchAPIBlob(
      `${API_BASE_URL}/files/download?path=${encodeURIComponent(tenantRelative(path))}`,
      path.split('/').filter(Boolean).pop() || 'download.bin',
    ),
};

export const backupsAPI = {
  /** List all backups belonging to the authenticated tenant. */
  list: (): Promise<BackupResponse[]> =>
    fetchAPI<BackupResponse[]>(`${API_BASE_URL}/backups`),

  /** Create a backup for a container. Synchronous — status is "success" or
   *  "failed" on return, but may be "pending"/"running" if the server queues it.
   *  Poll get() if status is not terminal.
   *
   *  `kind` is widened to `string` because the backend reserves
   *  "full" | "files" | "databases" | "mail" (v1 only implements "full" and
   *  returns 422 for the others). Surfacing the broader type in TS lets the
   *  UI render the picker today; runtime will surface the 422 if the user
   *  picks an unsupported scope. */
  create: (body: { container_id: number; kind: string }): Promise<BackupResponse> =>
    fetchAPI<BackupResponse>(`${API_BASE_URL}/backups`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** Create an ACCOUNT-scoped backup — every domain the tenant owns plus
   *  account metadata, bundled into ONE file. Synchronous. */
  createAccount: (): Promise<BackupResponse> =>
    fetchAPI<BackupResponse>(`${API_BASE_URL}/backups/account`, {
      method: 'POST',
    }),

  /** Restore an account-scoped backup from a multipart upload. Returns
   *  immediately with a RestoreJob; poll via getRestoreStatus. */
  restoreAccount: (file: File): Promise<RestoreJobResponse> => {
    const form = new FormData()
    form.append('file', file)
    return fetchAPI<RestoreJobResponse>(
      `${API_BASE_URL}/backups/account/restore`,
      { method: 'POST', body: form },
    )
  },

  /** Get a single backup (for status polling after create). */
  get: (id: number): Promise<BackupResponse> =>
    fetchAPI<BackupResponse>(`${API_BASE_URL}/backups/${id}`),

  /** Stream the backup tarball as a Blob with the server-provided filename. */
  download: (id: number): Promise<{ blob: Blob; filename: string }> =>
    fetchAPIBlob(
      `${API_BASE_URL}/backups/${id}/download`,
      `hpanel-backup-${id}.tar.gz`,
    ),

  /** Delete a backup record (on-disk tar is retained by the operator). */
  delete: (id: number): Promise<void> =>
    fetchAPI<void>(`${API_BASE_URL}/backups/${id}`, { method: 'DELETE' }),

  /** Upload a .tar.gz and begin an async restore job. Returns immediately with
   *  a restore_id; poll getRestoreStatus() until status is "success"/"failed". */
  restore: (file: File, containerId: number): Promise<RestoreJobResponse> => {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('container_id', String(containerId));
    return fetchAPI<RestoreJobResponse>(`${API_BASE_URL}/backups/restore`, {
      method: 'POST',
      body: form,
    });
  },

  /** Poll restore job status. */
  getRestoreStatus: (restoreId: number): Promise<RestoreJobResponse> =>
    fetchAPI<RestoreJobResponse>(`${API_BASE_URL}/backups/restores/${restoreId}`),
};

// ---- Account backups v3 (contract: docs/internal/docs/history/plans/backup-v3-api-contract.md) ----

export interface BackupV3 extends BackupResponse {
  /** "daily,weekly" for scheduled backups, otherwise null. */
  tiers: string | null;
  scheduled: boolean;
}

export interface BackupContents {
  backup_id: number;
  created_at: string | null;
  schema_version: number;
  domains: string[];
  home: string[];
  databases: string[];
  mailboxes: string[];
  has_settings: boolean;
}

export interface RestoreJobV3 {
  id: number;
  user_id: number | null;
  status: 'queued' | 'running' | 'success' | 'partial' | 'failed';
  error: string | null;
  report: { sections: Record<string, unknown>; errors: string[] } | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string | null;
}

export interface RestoreFilter {
  include?: string[];
  exclude?: string[];
}

export interface BackupScheduleSettings {
  enabled: boolean;
  run_at: string;
  weekly_day: number;
  monthly_day: number | 'last';
  keep_daily: number;
  keep_weekly: number;
  keep_monthly: number;
}

export interface BackupScheduleResponse {
  settings: BackupScheduleSettings;
  next_runs: Array<{ at: string; tiers: string[] }>;
  timezone: string;
}

function appendFilter(form: FormData, f: RestoreFilter): void {
  if (f.include && f.include.length) form.append('include', JSON.stringify(f.include));
  if (f.exclude && f.exclude.length) form.append('exclude', JSON.stringify(f.exclude));
}

/** Tenant v3 account-backup restore (selective). */
export const accountBackupsAPI = {
  contents: (backupId: number): Promise<BackupContents> =>
    fetchAPI<BackupContents>(`${API_BASE_URL}/backups/account/${backupId}/contents`),

  restore: (backupId: number, filter: RestoreFilter): Promise<RestoreJobV3> =>
    fetchAPI<RestoreJobV3>(`${API_BASE_URL}/backups/account/${backupId}/restore`, {
      method: 'POST',
      body: JSON.stringify(filter),
    }),

  restoreUpload: (file: File, filter: RestoreFilter = {}): Promise<RestoreJobV3> => {
    const form = new FormData();
    form.append('file', file, file.name);
    appendFilter(form, filter);
    return fetchAPI<RestoreJobV3>(`${API_BASE_URL}/backups/account/restore`, {
      method: 'POST',
      body: form,
    });
  },

  restoreStatus: (restoreId: number): Promise<RestoreJobV3> =>
    fetchAPI<RestoreJobV3>(`${API_BASE_URL}/backups/restores/${restoreId}`),
};

/** Admin account backups, full restore and schedule. */
export const adminBackupsAPI = {
  list: (username?: string): Promise<BackupV3[]> =>
    fetchAPI<BackupV3[]>(
      `${API_BASE_URL}/admin/backups/account${username ? `?username=${encodeURIComponent(username)}` : ''}`,
    ),

  backupNow: (username: string): Promise<BackupV3> =>
    fetchAPI<BackupV3>(
      `${API_BASE_URL}/admin/backups/account/${encodeURIComponent(username)}/backup`,
      { method: 'POST' },
    ),

  restore: (
    target: { username: string; backupId?: number; file?: File },
    filter: RestoreFilter = {},
  ): Promise<RestoreJobV3> => {
    const form = new FormData();
    form.append('target_username', target.username);
    if (target.file) form.append('file', target.file, target.file.name);
    else if (target.backupId !== undefined) form.append('backup_id', String(target.backupId));
    appendFilter(form, filter);
    return fetchAPI<RestoreJobV3>(`${API_BASE_URL}/admin/backups/account/restore`, {
      method: 'POST',
      body: form,
    });
  },

  restoreStatus: (restoreId: number): Promise<RestoreJobV3> =>
    fetchAPI<RestoreJobV3>(`${API_BASE_URL}/admin/backups/restores/${restoreId}`),

  getSchedule: (): Promise<BackupScheduleResponse> =>
    fetchAPI<BackupScheduleResponse>(`${API_BASE_URL}/admin/backups/schedule`),

  putSchedule: (settings: BackupScheduleSettings): Promise<BackupScheduleResponse> =>
    fetchAPI<BackupScheduleResponse>(`${API_BASE_URL}/admin/backups/schedule`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    }),
};

/**
 * Server-Sent Events helper.
 *
 * The Observability brief locks in SSE as the live-data transport for log
 * lines and stats samples. Browsers' native `EventSource`:
 *   - cannot carry an `Authorization: Bearer` header (so tenant auth has to
 *     ride on the impersonation cookie — hence `withCredentials: true`)
 *   - reconnects on its own with a fixed delay; we override that with
 *     exponential backoff (1s → 2s → 4s → 8s, capped at 8s) and cap the
 *     retry count so a permanently-degraded stream stops hammering the
 *     backend
 *   - silently follows server-sent `: keepalive` comments, so we don't
 *     need to do anything to handle them
 *
 * Per CLAUDE.md §9: every drop is `console.error`'d before the UI reacts;
 * the user sees only the dim status line in the LogTerminal, never a red
 * banner or a raw error string. After 5 consecutive failures, OR on a 429
 * from the concurrent-stream cap, the stream enters terminal `degraded`
 * state — the panel switches to manual refresh / Download mode.
 *
 * Test harness contract: pass `eventSourceCtor` to inject a mock
 * EventSource for Playwright. The hook also looks up
 * `window.__OBSERVABILITY_EVENTSOURCE_CTOR__` if no explicit override is
 * given, so the test harness can patch globally without touching every
 * call site.
 */

export type SSEStatus =
  | 'idle'          // not connected (e.g. enabled=false)
  | 'connecting'    // first attempt
  | 'open'          // healthy
  | 'reconnecting'  // backoff timer pending
  | 'degraded';     // terminal: cap reached or 429 received

export interface SSEHandle {
  /** Close the underlying EventSource (if any) and stop any pending retry. */
  close: () => void;
}

interface OpenSSEOptions {
  /** Override the EventSource constructor (test injection). */
  eventSourceCtor?: typeof EventSource;
  /** Called when status transitions. `errorDetail` is set on degraded. */
  onStatusChange?: (status: SSEStatus, errorDetail?: string) => void;
  /**
   * Single-channel dispatcher. The hook receives `(eventType, event)` for
   * every named event the server emits — both `message` (the default) and
   * any custom `event:` lines.
   */
  listeners: (eventType: string, event: MessageEvent) => void;
}

const MAX_BACKOFF_MS = 8000;
const MAX_CONSECUTIVE_FAILURES = 5;

export function openSSE(url: string, options: OpenSSEOptions): SSEHandle {
  const {
    onStatusChange,
    listeners,
    eventSourceCtor,
  } = options;

  const Ctor =
    eventSourceCtor ??
    (typeof window !== 'undefined'
      ? (window as unknown as {
          __OBSERVABILITY_EVENTSOURCE_CTOR__?: typeof EventSource;
        }).__OBSERVABILITY_EVENTSOURCE_CTOR__
      : undefined) ??
    EventSource;

  // Track which named events the server has emitted so we can re-attach
  // listeners after a reconnect. We always wire `message` + `error`; named
  // events are added lazily the first time we see them.
  let es: EventSource | null = null;
  //: Has this stream ever reached `onopen`? Governs whether a failure is worth
  //: probing: if we connected once, the endpoint plainly works and the drop is
  //: transient, so a probe would cost an extra request and tell us nothing.
  //: Only the never-opened case hides a status (404/429) worth recovering.
  let everOpened = false;
  let attempts = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let closed = false;
  const namedEvents = new Set<string>(['log', 'stats', 'error']);

  const transition = (next: SSEStatus, errorDetail?: string) => {
    onStatusChange?.(next, errorDetail);
  };

  const dispatch = (type: string, event: MessageEvent) => {
    try {
      listeners(type, event);
    } catch (err) {
      // A buggy handler must NOT take down the stream — log and continue.
      // eslint-disable-next-line no-console
      console.error('[openSSE] listener threw for', type, err);
    }
  };

  const cleanup = () => {
    if (es) {
      try { es.close(); } catch { /* ignore */ }
      es = null;
    }
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  };

  /**
   * Recover the HTTP status and `detail` that EventSource threw away.
   *
   * `EventSource.onerror` is opaque by design: no status, no body, no headers.
   * The server signals pre-stream failures as real HTTP statuses — 404 for a
   * domain the tenant does not own, 429 for the concurrent-stream cap — each
   * with a JSON `detail` (observability_views.py). Once the stream IS open it
   * switches to `event: error` frames, which EventSource CAN read; it is only
   * the pre-stream window that is invisible.
   *
   * The result was that the panel showed an empty skeleton and a generic
   * "Stats stream error" for every one of those, violating §9's requirement
   * that the server's detail reach the user. The 429 branch in
   * scheduleReconnect was unreachable code for the same reason — nothing could
   * ever supply `status`.
   *
   * One extra request per failure, and only on failure, so the cost is bounded
   * by the existing backoff.
   */
  const probeThenReconnect = async (reason: string): Promise<void> => {
    let status: number | undefined;
    let detail: string | undefined;
    try {
      const res = await fetch(url, {
        credentials: 'include',
        headers: { Accept: 'text/event-stream' },
      });
      status = res.status;
      if (!res.ok) {
        const body = await res.text();
        try {
          const parsed = JSON.parse(body);
          if (typeof parsed?.detail === 'string') detail = parsed.detail;
        } catch {
          // Non-JSON (an HTML gateway page) — leave detail undefined so the
          // caller falls back to a clean status-tied message rather than
          // rendering markup at the user. CLAUDE.md §9.
        }
      } else {
        // The probe succeeded, which means the original failure was transient
        // (or we just opened a second stream). Release it immediately so we
        // don't hold a server-side slot against the 4-stream cap.
        try {
          await res.body?.cancel();
        } catch {
          /* already closed */
        }
      }
    } catch {
      // Network-level failure — no status to report; fall through to backoff.
    }
    scheduleReconnect(reason, status, detail);
  };

  const scheduleReconnect = (reason: string, status?: number, detail?: string) => {
    cleanup();
    if (closed) return;

    // 429 — concurrent-stream cap. The server is telling us to stop; don't
    // hammer it. Switch to degraded so the panel surfaces the exact detail.
    if (status === 429) {
      // eslint-disable-next-line no-console
      console.error('[observability] SSE 429 (stream cap reached) for', url);
      transition('degraded',
        detail ??
        'Too many concurrent observability streams (max 4). Close other tabs or use the Download button.');
      return;
    }

    // Other 4xx are TERMINAL: a 404 for a domain you don't own, or a 403,
    // will not start working because we asked five more times. Degrade
    // immediately and show the server's own detail. 408/425 are the
    // exceptions — those genuinely mean "try again".
    if (status !== undefined && status >= 400 && status < 500
        && status !== 408 && status !== 425) {
      // eslint-disable-next-line no-console
      console.error('[observability] SSE terminal', status, 'for', url, detail);
      transition('degraded', detail ?? `Stream unavailable (HTTP ${status}).`);
      return;
    }

    attempts += 1;
    if (attempts > MAX_CONSECUTIVE_FAILURES) {
      // eslint-disable-next-line no-console
      console.error('[observability] SSE giving up after', attempts - 1, 'attempts:', url);
      transition('degraded',
        'Live streaming paused after repeated failures. Use Download for current logs.');
      return;
    }

    // 1s, 2s, 4s, 8s, 8s — exponential capped at MAX_BACKOFF_MS.
    const backoff = Math.min(MAX_BACKOFF_MS, 1000 * Math.pow(2, attempts - 1));
    // eslint-disable-next-line no-console
    console.error('[observability] SSE dropped (' + reason + '), retry #' + attempts + ' in', backoff, 'ms for', url);
    transition('reconnecting');
    retryTimer = setTimeout(() => {
      if (!closed) connect();
    }, backoff);
  };

  const connect = () => {
    if (closed) return;
    try {
      es = new Ctor(url, { withCredentials: true });
    } catch (err) {
      // Constructor throws are rare (malformed URL) — treat as a network
      // failure so we backoff rather than spin.
      // eslint-disable-next-line no-console
      console.error('[observability] EventSource constructor threw for', url, err);
      scheduleReconnect('ctor-threw');
      return;
    }

    es.onopen = () => {
      attempts = 0;
      everOpened = true;
      transition('open');
    };

    es.onmessage = (event) => dispatch('message', event);

    // Named events the server can emit. We attach a closure for each because
    // EventSource has no wildcard listener. `error` here is a server-sent
    // event named "error" with a JSON body — distinct from es.onerror, which
    // is the transport-level failure callback.
    namedEvents.forEach((name) => {
      es?.addEventListener(name, (event) => dispatch(name, event as MessageEvent));
    });

    // Transport-level failure. EventSource doesn't expose the response
    // status here — we have to probe via a one-shot fetch if we suspect
    // the cause is a 429. Cheaper: catch 429 server-side via the named
    // "error" event the contract specifies (server sends `event: error`
    // with `{"message": ...}` before closing).
    es.onerror = () => {
      // readyState CLOSED means the connection dropped and the browser
      // won't reconnect on its own; CONNECTING means EventSource is
      // already retrying. Either way, we take over with backoff — but FIRST
      // recover the status and detail the transport hid from us.
      if (everOpened) {
        // Mid-stream drop: the endpoint works, so there is no hidden status to
        // recover. Reconnect straight away rather than spending a request —
        // and, importantly, without issuing an extra call that a caller
        // counting requests would see as a reconnect that never happened.
        scheduleReconnect('transport-error');
        return;
      }
      void probeThenReconnect('transport-error');
    };

    // Server-sent `event: error` carrying a JSON detail. If the detail
    // says "Too many concurrent observability streams" we treat it as 429
    // and degrade immediately. Otherwise we let the transport-error path
    // handle it on the next disconnect.
    es.addEventListener('error', (event) => {
      const me = event as MessageEvent;
      if (typeof me.data !== 'string' || !me.data) return;
      try {
        const parsed = JSON.parse(me.data) as { message?: string };
        const msg = parsed?.message ?? '';
        if (msg.includes('Too many concurrent')) {
          // eslint-disable-next-line no-console
          console.error('[observability] SSE server reported concurrent-cap:', msg);
          closed = true;
          cleanup();
          transition('degraded', msg);
        }
      } catch {
        /* ignore non-JSON error events */
      }
    });
  };

  // Kick off the first attempt asynchronously so callers can attach status
  // listeners synchronously before any `connecting` transition fires.
  transition('connecting');
  // Use a microtask so the caller's setState happens before our first event.
  Promise.resolve().then(() => {
    if (!closed) connect();
  });

  return {
    close: () => {
      closed = true;
      cleanup();
      transition('idle');
    },
  };
}

/**
 * Trigger a download of `blob` as `filename` via a hidden <a download> link.
 * Centralized here so the resilience contract (CLAUDE.md §9) stays in
 * `client.ts` — callers don't need to touch DOM APIs to save a file.
 */
function triggerDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Defer revocation: some browsers race the click handler if you revoke
    // immediately. 60s is plenty for the browser to finish the save dialog.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  }
}

/**
 * Tenant Observability API — per-domain live logs + cgroup stats.
 *
 * Built to the locked W-* contract:
 *   GET  /api/v2/client/domains/{id}/observability/logs/stream    (SSE)
 *   GET  /api/v2/client/domains/{id}/observability/logs?tail=N    (text/plain attachment)
 *   GET  /api/v2/client/domains/{id}/observability/stats          (JSON)
 *   GET  /api/v2/client/domains/{id}/observability/stats/stream   (SSE)
 *
 * All routes use trailing-slash-free URLs to dodge the FastAPI
 * cross-origin-redirect trap (CLAUDE.md §9 backend-correctness notes).
 */
export const observabilityAPI = {
  /** One-shot stats snapshot (used by degraded-mode "Refresh" button). */
  snapshotStats: (domainId: number): Promise<TenantObservabilityStats> =>
    fetchAPI<TenantObservabilityStats>(
      `${API_BASE_URL}/v2/client/domains/${domainId}/observability/stats`,
    ),

  /**
   * Open the stats SSE stream. Returns a handle whose `close()` tears the
   * connection down — call it from useEffect cleanup.
   */
  streamStats: (
    domainId: number,
    handlers: {
      onStats: (stats: TenantObservabilityStats) => void;
      onError?: (message: string) => void;
      onStatusChange?: (status: SSEStatus, errorDetail?: string) => void;
    },
  ): SSEHandle =>
    openSSE(
      `${API_BASE_URL}/v2/client/domains/${domainId}/observability/stats/stream`,
      {
        onStatusChange: handlers.onStatusChange,
        listeners: (type, event) => {
          if (type === 'stats') {
            try {
              const parsed = JSON.parse(event.data) as TenantObservabilityStats;
              handlers.onStats(parsed);
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error('[observability] failed to parse stats payload', err);
            }
          } else if (type === 'error') {
            try {
              const parsed = JSON.parse(event.data) as { message?: string };
              handlers.onError?.(parsed?.message ?? 'Stats stream error');
            } catch {
              handlers.onError?.('Stats stream error');
            }
          }
        },
      },
    ),

  /** Open the log SSE stream. Same lifecycle contract as streamStats. */
  streamLogs: (
    domainId: number,
    handlers: {
      onLog: (entry: TenantObservabilityLogEvent) => void;
      onError?: (message: string) => void;
      onStatusChange?: (status: SSEStatus, errorDetail?: string) => void;
    },
  ): SSEHandle =>
    openSSE(
      `${API_BASE_URL}/v2/client/domains/${domainId}/observability/logs/stream`,
      {
        onStatusChange: handlers.onStatusChange,
        listeners: (type, event) => {
          if (type === 'log') {
            try {
              const parsed = JSON.parse(event.data) as TenantObservabilityLogEvent;
              handlers.onLog(parsed);
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error('[observability] failed to parse log payload', err);
            }
          } else if (type === 'error') {
            try {
              const parsed = JSON.parse(event.data) as { message?: string };
              handlers.onError?.(parsed?.message ?? 'Log stream error');
            } catch {
              handlers.onError?.('Log stream error');
            }
          }
        },
      },
    ),

  /**
   * Pull the last `tail` lines as a text file and trigger a browser
   * download. The backend sets Content-Disposition: attachment; we ride on
   * fetchAPIBlob for the resilience guarantees and then dispatch the
   * download via a hidden link.
   */
  downloadLogs: async (domainId: number, tail: number): Promise<void> => {
    const clamped = Math.max(1, Math.min(5000, Math.floor(tail)));
    const { blob, filename } = await fetchAPIBlob(
      `${API_BASE_URL}/v2/client/domains/${domainId}/observability/logs?tail=${clamped}`,
      `domain-${domainId}-logs.txt`,
    );
    triggerDownload(blob, filename);
  },
};

/**
 * Tenant Container Observability API
 *
 * Read-only access for the calling tenant: container list, logs tail, and
 * an interactive console (WebSocket).
 */
export interface MyContainer {
  id: number;
  name: string;
  backend_kind: 'docker' | 'lxc';
  status: string | null;
}

export const containerAPI = {
  /** GET /api/client/container/list — caller's containers. */
  listMine(): Promise<MyContainer[]> {
    return fetchAPI<MyContainer[]>(`${API_BASE_URL}/client/container/list`);
  },

  /** GET /api/client/container/{id}/logs?tail=N */
  logs(id: number, tail: number): Promise<{ logs: string }> {
    const params = new URLSearchParams({ tail: String(tail) });
    return fetchAPI<{ logs: string }>(
      `${API_BASE_URL}/client/container/${id}/logs?${params}`,
    );
  },

  /** Build a WebSocket URL for the console.
   *  WebSocket is NOT routed through fetchAPI — see ConsolePanel.tsx
   *  for error translation that mirrors §9 on the open-attempt. */
  consoleUrl(id: number, cols: number, rows: number): string {
    const httpBase = new URL(API_BASE_URL, window.location.href);
    const wsProto = httpBase.protocol === 'https:' ? 'wss:' : 'ws:';
    const params = new URLSearchParams({ cols: String(cols), rows: String(rows) });
    return `${wsProto}//${httpBase.host}${httpBase.pathname}/client/container/${id}/console?${params}`;
  },
};

/**
 * Export all API modules as a single object.
 */
export const api = {
  auth: authAPI,
  system: systemAPI,
  users: usersAPI,
  settings: settingsAPI,
  containers: containersAPI,
  container: containerAPI,
  client: clientAPI,
  domains: domainsAPI,
  clientDatabases: clientDatabasesAPI,
  certificates: certificatesAPI,
  runtime: runtimeAPI,
  packages: packagesAPI,
  resellers: resellersAPI,
  whiteLabel: whiteLabelAPI,
  gdpr: gdprAPI,
  metrics: metricsAPI,
  dns: dnsAPI,
  wordpress: wordpressAPI,
  email: emailAPI,
  imageCatalog: imageCatalogAPI,
  migration: migrationAPI,
  backups: backupsAPI,
  files: filesAPI,
  observability: observabilityAPI,
  proxy: proxyAPI,
};

export default api;

/**
 * Panel self-update (admin).
 * - `getUpdateStatus`  GET  /api/admin/updates
 * - `requestUpgrade`   POST /api/admin/updates/apply   {version}
 * - `setUpdatePolicy`  PUT  /api/admin/updates/policy  {policy}
 */
export type UpdatePolicy = 'notify' | 'auto-patch' | 'auto'

export interface UpdateLastApply {
  started_at: string
  finished_at: string | null
  from: string
  to: string
  ok: boolean
  rolled_back: boolean
  error: string | null
}

export interface UpdateStatus {
  configured: boolean
  checked_at: string | null
  current: string | null
  latest: string | null
  available: boolean
  kind: 'patch' | 'minor' | 'major' | null
  notes_url: string | null
  policy: UpdatePolicy
  error: string | null
  applying: boolean
  last_apply: UpdateLastApply | null
}

export const getUpdateStatus = (): Promise<UpdateStatus> =>
  fetchAPI<UpdateStatus>(`${API_BASE_URL}/admin/updates`)

export const requestUpgrade = (
  version: string,
): Promise<{ version: string; requested: boolean }> =>
  fetchAPI<{ version: string; requested: boolean }>(
    `${API_BASE_URL}/admin/updates/apply`,
    { method: 'POST', body: JSON.stringify({ version }) },
  )

export const setUpdatePolicy = (
  policy: UpdatePolicy,
): Promise<{ policy: UpdatePolicy }> =>
  fetchAPI<{ policy: UpdatePolicy }>(`${API_BASE_URL}/admin/updates/policy`, {
    method: 'PUT',
    body: JSON.stringify({ policy }),
  })

/**
 * Automatic security updates for catalog runtime images (admin).
 * - GET  /api/admin/image-updates                 status
 * - PUT  /api/admin/image-updates/settings        policy / window / pause
 * - POST /api/admin/image-updates/check           run a check (background)
 * - POST /api/admin/image-updates/{id}/apply      apply a ready update
 * - POST /api/admin/image-updates/{id}/resume     continue a halted rollout
 * - POST /api/admin/image-updates/advance         move sites now
 * Tenant: GET/PUT /api/runtime/{container_id}/updates (pin a site).
 */
export type ImageUpdatePolicy = 'auto' | 'notify'

export interface ImageUpdateSettings {
  policy: ImageUpdatePolicy
  window_start: string
  window_end: string
  paused: boolean
  last_check_at: string | null
  last_check_error: string | null
}

export interface ImageUpdateSite {
  id: number
  container_id: number
  container_name: string
  status: 'pending' | 'updated' | 'rolled_back' | 'rollback_failed' | 'pinned' | 'skipped'
  detail: string | null
  updated_at: string | null
}

export interface ImageUpdateScan {
  error?: string
  current?: { counts: Record<string, number> }
  candidate?: { counts: Record<string, number> }
  fixed?: Record<string, string>
  introduced?: Record<string, string>
}

export interface ImageUpdateCandidate {
  id: number
  image_id: number
  image_name: string | null
  current_ref: string
  candidate_ref: string
  current_version: string | null
  candidate_version: string | null
  line_ok: boolean
  preflight: { ok: boolean; detail: string; missing: string[] } | null
  scan: ImageUpdateScan | null
  status: 'blocked' | 'ready' | 'queued' | 'rolling_out' | 'halted' | 'done' | 'superseded'
  detail: string | null
  requested_by: string | null
  auto_eligible: boolean
  created_at: string | null
  sites: ImageUpdateSite[]
  site_counts: Record<string, number>
}

export interface ImageUpdatesStatus {
  settings: ImageUpdateSettings
  stale: boolean
  checking: boolean
  candidates: ImageUpdateCandidate[]
  untracked: { image: string; containers: { id: number; name: string; pinned: boolean }[] }[]
}

export interface SiteUpdates {
  pinned: boolean
  history: { status: string; detail: string | null; from_version: string | null; to_version: string | null; updated_at: string | null }[]
}

export const imageUpdatesAPI = {
  status: (): Promise<ImageUpdatesStatus> =>
    fetchAPI<ImageUpdatesStatus>(`${API_BASE_URL}/admin/image-updates`),
  saveSettings: (body: Partial<Pick<ImageUpdateSettings, 'policy' | 'window_start' | 'window_end' | 'paused'>>): Promise<ImageUpdateSettings> =>
    fetchAPI<ImageUpdateSettings>(`${API_BASE_URL}/admin/image-updates/settings`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  check: (): Promise<{ started: boolean }> =>
    fetchAPI<{ started: boolean }>(`${API_BASE_URL}/admin/image-updates/check`, { method: 'POST' }),
  apply: (id: number): Promise<ImageUpdateCandidate> =>
    fetchAPI<ImageUpdateCandidate>(`${API_BASE_URL}/admin/image-updates/${id}/apply`, { method: 'POST' }),
  resume: (id: number): Promise<ImageUpdateCandidate> =>
    fetchAPI<ImageUpdateCandidate>(`${API_BASE_URL}/admin/image-updates/${id}/resume`, { method: 'POST' }),
  advance: (maxSites = 1): Promise<{ results: ImageUpdateSite[] }> =>
    fetchAPI<{ results: ImageUpdateSite[] }>(`${API_BASE_URL}/admin/image-updates/advance`, {
      method: 'POST',
      body: JSON.stringify({ max_sites: maxSites }),
    }),
  siteUpdates: (containerId: number): Promise<SiteUpdates> =>
    fetchAPI<SiteUpdates>(`${API_BASE_URL}/runtime/${containerId}/updates`),
  setPinned: (containerId: number, pinned: boolean): Promise<SiteUpdates> =>
    fetchAPI<SiteUpdates>(`${API_BASE_URL}/runtime/${containerId}/updates`, {
      method: 'PUT',
      body: JSON.stringify({ pinned }),
    }),
}

/** Tenant hosting slots (names hosted on the tenant's containers). */
export const slotsAPI = {
  list: (): Promise<{ slots: Slot[] }> =>
    fetchAPI<{ slots: Slot[] }>(`${API_BASE_URL}/client/slots`),
  addSubdomain: (
    containerId: number,
    body: { parent_domain_id: number; name: string },
  ): Promise<Slot> =>
    fetchAPI<Slot>(`${API_BASE_URL}/client/slots/${containerId}/subdomains`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  removeName: (domainId: number): Promise<{ removed: string[] }> =>
    fetchAPI<{ removed: string[] }>(`${API_BASE_URL}/client/slots/names/${domainId}`, {
      method: 'DELETE',
    }),
};

/** Tenant requests for a new slot or parked domain (admin approves). */
export const domainRequestsAPI = {
  list: (): Promise<{ requests: DomainRequest[] }> =>
    fetchAPI<{ requests: DomainRequest[] }>(`${API_BASE_URL}/client/domain-requests`),
  submit: (body: {
    kind: DomainRequestKind;
    name: string;
    container_id?: number;
    image?: string;
  }): Promise<DomainRequest> =>
    fetchAPI<DomainRequest>(`${API_BASE_URL}/client/domain-requests`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  cancel: (id: number): Promise<DomainRequest> =>
    fetchAPI<DomainRequest>(`${API_BASE_URL}/client/domain-requests/${id}/cancel`, {
      method: 'POST',
    }),
};

/** Admin slot management and domain-request decisions. */
export const adminSlotsAPI = {
  list: (userId?: number): Promise<{ slots: Slot[] }> =>
    fetchAPI<{ slots: Slot[] }>(`${API_BASE_URL}/admin/slots${userId ? `?user_id=${userId}` : ''}`),
  create: (body: {
    user_id: number;
    name: string;
    image?: string;
    force?: boolean;
    backend_kind?: 'docker' | 'lxc';
  }): Promise<Slot> =>
    fetchAPI<Slot>(`${API_BASE_URL}/admin/slots`, { method: 'POST', body: JSON.stringify(body) }),
  addParked: (containerId: number, body: { name: string; force?: boolean }): Promise<Slot> =>
    fetchAPI<Slot>(`${API_BASE_URL}/admin/slots/${containerId}/parked`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  addSubdomain: (
    containerId: number,
    body: { parent_domain_id: number; name: string; force?: boolean },
  ): Promise<Slot> =>
    fetchAPI<Slot>(`${API_BASE_URL}/admin/slots/${containerId}/subdomains`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  removeName: (domainId: number): Promise<{ removed: string[] }> =>
    fetchAPI<{ removed: string[] }>(`${API_BASE_URL}/admin/slots/names/${domainId}`, {
      method: 'DELETE',
    }),
  recreate: (containerId: number): Promise<{ container_id: number }> =>
    fetchAPI<{ container_id: number }>(`${API_BASE_URL}/admin/slots/${containerId}/recreate`, {
      method: 'POST',
    }),
  requests: (status = 'pending'): Promise<{ requests: DomainRequest[] }> =>
    fetchAPI<{ requests: DomainRequest[] }>(`${API_BASE_URL}/admin/domain-requests?status=${status}`),
  approve: (id: number, force = false): Promise<DomainRequest> =>
    fetchAPI<DomainRequest>(`${API_BASE_URL}/admin/domain-requests/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ force }),
    }),
  reject: (id: number, reason: string): Promise<DomainRequest> =>
    fetchAPI<DomainRequest>(`${API_BASE_URL}/admin/domain-requests/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
};

/** Admin: every site name's certificate (read-only). */
export const adminCertificatesAPI = {
  overview: (): Promise<SiteCertificateOverview> =>
    fetchAPI<SiteCertificateOverview>(`${API_BASE_URL}/admin/certificates`),
};
